import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/layout/TopBar";
import Footer from "@/components/layout/Footer";
import {
  ArrowRight, Truck, ShieldCheck, Package, Plane, Anchor,
  Users, Phone, Mail, Zap, Star, Calendar, ChevronRight, ChevronLeft, CheckCircle2,
  Globe, Route, MapPin,
} from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen relative overflow-x-hidden">

      <div className="relative z-10">

        {/* ── Hero band: the video background wraps BOTH the TopBar and the hero
               content, so it always covers the content no matter how tall the
               hero grows (prevents CTAs spilling below the video on shorter /
               MacBook viewports). The rest of the page scrolls onto the
               site-wide dark backdrop (body bg). ─────────────────────────── */}
        <div className="relative">
          {/* Hero Video Background — absolutely fills this band */}
          <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
            <video
              autoPlay muted loop playsInline
              className="w-full h-full object-cover"
            >
              <source src="/assets/hero-video.mp4" type="video/mp4" />
            </video>
            {/* Very subtle overlay — just enough for text legibility, deepening
                slightly toward the bottom to blend into the dark section below. */}
            <div
              className="absolute inset-0"
              style={{ background: "linear-gradient(180deg, rgba(5,5,10,0.12) 0%, rgba(5,5,10,0.10) 60%, rgba(10,10,15,0.45) 100%)" }}
            />
          </div>

          <div className="relative z-10">
            <TopBar />

            {/* ── HERO ──────────────────────────────────────────── */}
            <section
              className="relative flex flex-col items-start justify-center px-6 sm:px-10 lg:px-16 text-left py-8"
              style={{ minHeight: "calc(100svh - 80px)" }}
            >
              {/* Left-aligned content column */}
              <div className="w-full max-w-3xl flex flex-col items-start">

            {/* Badge */}
            <div className="inline-flex items-center gap-2 rounded-full border border-[#19e6c8]/40 bg-[#19e6c8]/5 px-4 py-1.5 mb-5 fade-up">
              <MapPin size={13} className="text-[#19e6c8]" />
              <span className="font-display text-[#19e6c8] text-xs tracking-[0.3em]">BRISBANE, AUSTRALIA</span>
            </div>

            {/* Heading */}
            <h1
              className="brand-title rqnd-pro text-6xl sm:text-7xl md:text-7xl xl:text-[5.5rem] leading-[0.88] uppercase"
              style={{ textShadow: "0 2px 22px rgba(0,0,0,0.4)" }}
            >
              <span className="text-white anim-shift-down">YOUR<br />FREIGHT,</span><br />
              {/* "OUR MISSION!" — smooth typewriter intro: types out
                  character-by-character with a softly blinking caret. */}
              <Typewriter
                text={"OUR\nMISSION!"}
                className="mission-word"
                style={{ color: "#CAF92C" }}
                startDelay={550}
                speed={95}
              />
            </h1>

            {/* Sub-text */}
            <p
              className="text-white/85 mt-4 text-base sm:text-lg max-w-xl leading-relaxed fade-up"
              style={{ textShadow: "0 1px 14px rgba(0,0,0,0.45)" }}
            >
              Australia's one-stop solution for road freight transport.<br />
              Reliable, affordable and built around your urgency.
            </p>

            {/* CTA row */}
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3 fade-up">
              <Link to="/login" className="pill-btn pill-btn-lime flex items-center gap-2 !text-base !px-9 !py-3.5">
                Get Started
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/track"
                className="rounded-full border border-[#c0c0c0]/70 hover:border-[#e6e6e6] hover:bg-white/5 px-8 py-3.5 font-display tracking-widest text-sm flex items-center gap-2 text-white/85 hover:text-white transition-all uppercase"
              >
                Track Shipment
              </Link>
              <a
                href="tel:+61470344648"
                className="rounded-full border border-[#19e6c8]/60 hover:border-[#19e6c8] hover:bg-[#19e6c8]/10 px-8 py-3.5 font-display tracking-widest text-sm flex items-center gap-2 text-[#19e6c8] transition-all uppercase"
              >
                <Phone size={16} />
                Call Now
              </a>
            </div>

            <p className="text-white/40 text-xs mt-4 font-display tracking-[0.25em] fade-up uppercase">
              MON – FRI 9AM – 5PM &nbsp;&nbsp; SAT – SUN BY APPOINTMENT
            </p>
              </div>
            </section>
          </div>{/* end TopBar + hero (over video) */}
        </div>{/* end hero band (video bg) */}

        {/* Dark base — the rest of the page scrolls onto the site-wide backdrop */}
        <div className="relative z-10">

        {/* ── Stats strip (relocated below the fold) ────────── */}
        <section className="max-w-[1400px] mx-auto px-6 lg:px-8 mt-8 pb-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <HeroStat icon={Package}     label="Jobs Delivered" value="5,000+"  tint="#19e6c8" />
            <HeroStat icon={ShieldCheck} label="On-Time Rate"   value="100%"    tint="#22c55e" />
            <HeroStat icon={Zap}         label="Pallets Moved"  value="15,000+" tint="#22c55e" />
            <HeroStat icon={Users}       label="Happy Clients"  value="20+"     tint="#3b82f6" />
          </div>
        </section>

        {/* ── SERVICES ──────────────────────────────────────── */}
        <section id="services" className="relative py-28">

          <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <p className="font-display tracking-[0.4em] text-[#19e6c8] text-sm">WHAT WE OFFER</p>
              <h2 className="brand-title text-4xl md:text-5xl mt-3">Our Services</h2>
              <p className="text-white/65 mt-4 text-lg">
                Flexible freight solutions for every business need — from local airfreight and LCL ad-hoc to nationwide B-double freight.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <ServiceCard
                icon={Package}
                title="Standard / General"
                tag="ROAD FREIGHT"
                tagColor="#19e6c8"
                desc="Ad-hoc - Local general freight deliveries at the best rates – pallets, crates, cases etc. Book at least a day ahead for business-hours delivery. Tail lift drop-off available."
                color="#19e6c8"
              />
              <ServiceCard
                icon={Users}
                title="Permanent Hire"
                tag="MOST POPULAR"
                tagColor="#8a5cff"
                desc="Dedicated drivers working exclusively for your business. Eliminate fleet ownership and maintenance costs entirely."
                color="#8a5cff"
              />
              <ServiceCard
                icon={Anchor}
                title="LCL / Airfreight"
                tag="SEA & AIR"
                tagColor="#3b82f6"
                desc="Air and Sea freight transport specialists with vast industry experience handling time sensitive import pickups and export drop-offs, booking and managing timeslots."
                color="#3b82f6"
              />
              <ServiceCard
                icon={Truck}
                title="FCL"
                tag="FULL CONTAINER"
                tagColor="#6366f1"
                desc="Wharf import and export for Full Container loads – Skel, Sideloader and Tilt-tray services handled efficiently with the help of our dedicated associate partners."
                color="#6366f1"
              />
              <ServiceCard
                icon={Route}
                title="Interstate B-double"
                tag="NATIONWIDE"
                tagColor="#f59e0b"
                desc="Full b-double interstate loads handled with ease via our trusted and reliable affiliate partner company at affordable rates."
                color="#f59e0b"
              />
              <ServiceCard
                icon={Globe}
                title="Freight Forwarding Services"
                tag="MULTI-MODAL"
                tagColor="#22c55e"
                desc="Whether it's import–export, customs clearance or compliance – we make it all come together with our trusted partners."
                color="#22c55e"
              />
              <ServiceCard
                icon={Zap}
                title="Same Day Express"
                tag="FAST"
                tagColor="#f97316"
                desc="Book same day, delivered before close of business. We understand that your urgency is our priority."
                color="#f97316"
              />
              <ServiceCard
                icon={Star}
                title="VIP"
                tag="3–4 HOURS"
                tagColor="#a855f7"
                desc="Delivery within 3–4 hours of confirmed booking. Because time equals money — and we get that."
                color="#a855f7"
              />
              <ServiceCard
                icon={Calendar}
                title="Hourly / Custom"
                tag="FLEXIBLE"
                tagColor="#ff6b6b"
                desc="Hourly hire, pallet rates, and fully customised trucking solutions tailored to your unique requirements."
                color="#ff6b6b"
              />
            </div>

            <div className="text-center mt-12">
              <Link
                to="/services"
                className="inline-flex items-center gap-2 text-[#19e6c8] font-display tracking-widest text-sm hover:gap-3 transition-all group"
              >
                EXPLORE ALL SERVICES
                <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>

        {/* ── WHY DRIVN ─────────────────────────────────────── */}
        <section className="max-w-[1400px] mx-auto px-6 lg:px-8 py-28">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Carousel column */}
            <div className="relative order-2 lg:order-1">
              <TruckCarousel />
            </div>

            {/* Content column */}
            <div className="order-1 lg:order-2">
              <p className="font-display tracking-[0.4em] text-[#19e6c8] text-sm">WHY CHOOSE DRIVN</p>
              <h2 className="brand-title text-4xl md:text-5xl mt-3">Built on Reliability, Ethics and Trust</h2>
              <p className="text-white/70 mt-4 text-lg leading-relaxed">
                <span className="font-semibold text-white">Reliable Freight Solutions</span><br />
                End to end freight solutions – We can handle freight forwarding, customs and transport for you
              </p>

              <div className="mt-8 space-y-5">
                {[
                  {
                    title: "Australia Local Experts",
                    desc: "We know every road, every route, and every shortcut across Australia. Local knowledge at a national scale.",
                  },
                  {
                    title: "Your Urgency Is Our Priority",
                    desc: "From same-day express to VIP 3–4 hour delivery — we move when you need us to, no excuses.",
                  },
                  {
                    title: "Reliable Freight Solutions",
                    desc: "From standard road freight to B-double interstate runs — our fleet and driver network is built to handle any load, any distance.",
                  },
                  {
                    title: "Trusted by 20+ Businesses",
                    desc: "From SMEs to growing enterprises — DRIVN is the freight partner Australian businesses rely on for consistent, on-time delivery.",
                  },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 group">
                    <CheckCircle2 size={22} className="text-[#19e6c8] shrink-0 mt-0.5" />
                    <div>
                      <h4 className="brand-title text-base group-hover:text-[#19e6c8] transition-colors">{item.title}</h4>
                      <p className="text-white/55 text-sm mt-0.5 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/about" className="pill-btn flex items-center gap-2">
                  About Us <ArrowRight size={16} />
                </Link>
                <a
                  href="tel:+61470344648"
                  className="rounded-full border border-white/20 hover:border-[#19e6c8]/50 px-6 py-3 font-display tracking-widest text-xs transition-all flex items-center gap-2"
                >
                  <Phone size={13} /> +61 470 344 648
                </a>
                <a
                  href="mailto:hello@drivnfreight.com.au"
                  className="rounded-full border border-[#19e6c8]/40 hover:border-[#19e6c8] bg-[#19e6c8]/5 hover:bg-[#19e6c8]/10 px-6 py-3 font-display tracking-widest text-xs transition-all flex items-center gap-2 text-[#19e6c8]"
                >
                  <Mail size={13} /> Email us
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ──────────────────────────────────── */}
        <section className="relative py-28">
          <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <p className="font-display tracking-[0.4em] text-[#19e6c8] text-sm">THE PROCESS</p>
              <h2 className="brand-title text-4xl md:text-5xl mt-3">How It Works</h2>
              <p className="text-white/65 mt-4 text-lg">
                From booking to proof of delivery — a seamless freight experience in four simple steps.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                {
                  n: "01", icon: Package, title: "Quote and Book",
                  desc: "Free quote via call/email, then book using our client portal or email. We review, assign a driver, and confirm — fast.",
                },
                {
                  n: "02", icon: Truck, title: "Pickup",
                  desc: "Your driver receives the consignment note and departs on schedule. Live-tracked from the moment they move.",
                },
                {
                  n: "03", icon: ShieldCheck, title: "Deliver & Prove",
                  desc: "Driver captures POD photos and receiver signature in-app. You see confirmation the moment it's done.",
                },
                {
                  n: "04", icon: CheckCircle2, title: "Invoice",
                  desc: "Tax invoice auto-generated from job data, emailed to you and synced to accounts. Zero admin on your end.",
                },
              ].map(({ n, icon: Icon, title, desc }) => (
                <div
                  key={n}
                  className="rounded-2xl p-7 border border-white/10 hover:border-[#19e6c8]/40 transition-all group backdrop-blur-md"
                  style={{ background: "rgba(255,255,255,0.025)" }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <span className="brand-title text-4xl text-[#19e6c8]/25 group-hover:text-[#19e6c8]/70 transition-colors">
                      {n}
                    </span>
                    <span
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: "#19e6c822", color: "#19e6c8" }}
                    >
                      <Icon size={20} />
                    </span>
                  </div>
                  <h3 className="brand-title text-xl">{title}</h3>
                  <p className="text-white/55 text-sm mt-2 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── URGENCY STRIP ─────────────────────────────────── */}
        <section className="py-16 px-6 lg:px-8">
          <div className="max-w-[1400px] mx-auto">
            <div className="rounded-3xl overflow-hidden relative border border-white/10">
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(120deg, rgba(111,76,255,0.18), rgba(43,95,255,0.14))" }}
              />

              <div className="relative z-10 grid md:grid-cols-3 gap-6 p-6 sm:p-10 md:p-14">
                <div className="md:col-span-2">
                  <div className="inline-flex items-center gap-2 rounded-full bg-[#f59e0b]/10 border border-[#f59e0b]/30 px-4 py-1.5 mb-5">
                    <span className="w-2 h-2 rounded-full bg-[#f59e0b] animate-pulse" />
                    <span className="font-display text-[#f59e0b] text-xs tracking-[0.3em]">SAME DAY AVAILABLE</span>
                  </div>
                  <h3 className="brand-title text-3xl md:text-4xl">Need it there today?</h3>
                  <p className="text-white/70 mt-3 text-lg max-w-xl leading-relaxed">
                    Book before midday and we'll have your freight delivered before close of business. Need it faster? Our VIP service delivers in just 3–4 hours.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
                    <div className="flex items-center gap-2 text-white/55 text-sm">
                      <CheckCircle2 size={15} className="text-[#19e6c8]" />
                      Mon–Fri: 7:00 AM – 9:00 AM
                    </div>
                    <div className="flex items-center gap-2 text-white/55 text-sm">
                      <CheckCircle2 size={15} className="text-[#19e6c8]" />
                      Sat–Sun: By Appointment
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3 justify-center">
                  <a href="tel:+61470344648" className="pill-btn flex items-center justify-center gap-2 !text-base">
                    <Phone size={16} /> +61 470 344 648
                  </a>
                  <a
                    href="mailto:hello@drivnfreight.com.au"
                    className="rounded-full border border-white/20 hover:border-[#19e6c8]/50 px-6 py-3 font-display tracking-widest text-xs flex items-center justify-center gap-2 transition-all"
                  >
                    <Mail size={14} /> EMAIL US
                  </a>
                  <Link
                    to="/track"
                    className="rounded-full border border-white/20 hover:border-white/40 px-6 py-3 font-display tracking-widest text-xs flex items-center justify-center gap-2 transition-all text-white/65"
                  >
                    TRACK A SHIPMENT
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── FINAL CTA ─────────────────────────────────────── */}
        <section className="max-w-[1400px] mx-auto px-6 lg:px-8 py-16 pb-28">
          <div
            className="rounded-3xl p-6 sm:p-10 md:p-16 text-center relative overflow-hidden border border-white/10"
            style={{ background: "linear-gradient(135deg, rgba(111,76,255,0.14), rgba(43,95,255,0.10), rgba(25,230,200,0.06))" }}
          >
            {/* Glow orb */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div
                className="w-[500px] h-[500px] rounded-full opacity-10"
                style={{ background: "radial-gradient(circle, #6f4cff, transparent 70%)" }}
              />
            </div>

            <div className="relative z-10">
              <p className="font-display tracking-[0.4em] text-[#19e6c8] text-sm mb-4">GET STARTED TODAY</p>
              <h2 className="brand-title text-4xl md:text-5xl">Ready to Move Your Freight?</h2>
              <p className="text-white/65 mt-4 text-xl max-w-2xl mx-auto leading-relaxed">
                Join 20+ Australian businesses that trust DRIVN FREIGHT for reliable, affordable road freight transport.
              </p>
              <div className="mt-10 flex flex-wrap gap-4 justify-center">
                <Link to="/login" className="pill-btn flex items-center gap-2 !text-base !px-8 !py-4">
                  Start Now <ArrowRight size={18} />
                </Link>
                <Link
                  to="/services"
                  className="rounded-full border border-white/25 hover:border-[#19e6c8]/50 hover:bg-[#19e6c8]/5 px-8 py-4 font-display tracking-widest text-sm transition-all"
                >
                  OUR SERVICES
                </Link>
                <Link
                  to="/about"
                  className="rounded-full border border-white/25 hover:border-[#19e6c8]/50 hover:bg-[#19e6c8]/5 px-8 py-4 font-display tracking-widest text-sm transition-all"
                >
                  ABOUT US
                </Link>
              </div>
            </div>
          </div>
        </section>

        <Footer />
        </div>{/* end dark-base wrapper */}
      </div>
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────── */

const CAROUSEL_SLIDES = [
  { type: "video", src: "/assets/carousel/IMG_0553.mp4" },
  { type: "video", src: "/assets/carousel/IMG_8816.mp4" },
  { type: "image", src: "/assets/carousel/IMG_0795.JPG" },
  { type: "image", src: "/assets/carousel/IMG_0798.JPG" },
  { type: "image", src: "/assets/carousel/IMG_0800.JPG" },
  { type: "image", src: "/assets/carousel/IMG_1435.JPG" },
  { type: "image", src: "/assets/carousel/IMG_1436.JPG" },
  { type: "image", src: "/assets/carousel/IMG_1961.jpg" },
  { type: "image", src: "/assets/carousel/IMG_5049.jpg" },
];

/* Smooth typewriter — reveals `text` one character at a time after an
   optional delay. The FULL phrase is always rendered (untyped characters
   are just transparent) so the box never changes size and surrounding
   content never shifts. "\n" renders as a line break. */
function Typewriter({ text, className, style, speed = 90, startDelay = 0 }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    setCount(0);
    let i = 0;
    let interval;
    const start = setTimeout(() => {
      interval = setInterval(() => {
        i += 1;
        setCount(i);
        if (i >= text.length) clearInterval(interval);
      }, speed);
    }, startDelay);
    return () => { clearTimeout(start); clearInterval(interval); };
  }, [text, speed, startDelay]);

  return (
    <span className={className} style={style}>
      {text.split("").map((ch, i) =>
        ch === "\n" ? (
          <br key={i} />
        ) : (
          <span
            key={i}
            style={{ opacity: i < count ? 1 : 0, transition: "opacity 120ms ease-out" }}
          >
            {ch}
          </span>
        )
      )}
    </span>
  );
}

function TruckCarousel() {
  const [active, setActive] = useState(0);
  const videoRefs = useRef({});

  const goTo = i => setActive((i + CAROUSEL_SLIDES.length) % CAROUSEL_SLIDES.length);
  const prev = () => goTo(active - 1);
  const next = () => goTo(active + 1);

  const activeIsVideo = CAROUSEL_SLIDES[active].type === "video";

  useEffect(() => {
    // Videos advance when they finish playing (see onEnded); images auto-advance on a timer.
    if (activeIsVideo) return;
    const id = setInterval(() => setActive(p => (p + 1) % CAROUSEL_SLIDES.length), 4000);
    return () => clearInterval(id);
  }, [active, activeIsVideo]);

  useEffect(() => {
    // Restart the active video from the top whenever it becomes active.
    const vid = videoRefs.current[active];
    if (vid) {
      vid.currentTime = 0;
      vid.play().catch(() => {});
    }
  }, [active]);

  return (
    <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-2xl">
      {CAROUSEL_SLIDES.map((slide, i) =>
        slide.type === "video" ? (
          <video
            key={i}
            ref={el => { videoRefs.current[i] = el; }}
            src={slide.src}
            muted
            playsInline
            autoPlay={i === active}
            onEnded={() => i === active && next()}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
            style={{ opacity: i === active ? 1 : 0 }}
          />
        ) : (
          <img
            key={i}
            src={slide.src}
            alt={`DRIVN fleet ${i + 1}`}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
            style={{ opacity: i === active ? 1 : 0 }}
          />
        )
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

      {/* Prev / Next arrows */}
      <button
        onClick={prev}
        aria-label="Previous slide"
        className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center bg-black/45 hover:bg-black/70 border border-white/15 text-white backdrop-blur-sm transition-colors"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        onClick={next}
        aria-label="Next slide"
        className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center bg-black/45 hover:bg-black/70 border border-white/15 text-white backdrop-blur-sm transition-colors"
      >
        <ChevronRight size={20} />
      </button>

    </div>
  );
}

function HeroStat({ icon: Icon, label, value, tint }) {
  return (
    <div
      className="rounded-xl px-4 py-5 border border-white/10 backdrop-blur-sm hover:border-[#22c55e]/50 transition-all group flex flex-col items-center text-center gap-2"
      style={{ background: "rgba(255,255,255,0.04)" }}
    >
      <span
        className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors group-hover:!bg-[#22c55e]/20 group-hover:!text-[#22c55e]"
        style={{ background: `${tint}22`, color: tint }}
      >
        <Icon size={17} />
      </span>
      <p className="brand-title text-2xl sm:text-3xl leading-none transition-colors duration-300 group-hover:text-[#22c55e]">
        {value}
      </p>
      <p className="text-white/50 text-[10px] uppercase tracking-wide leading-tight group-hover:text-white/70 transition-colors">{label}</p>
    </div>
  );
}

function ServiceCard({ icon: Icon, title, tag, tagColor, desc, color }) {
  return (
    <div
      className="rounded-2xl p-7 border border-white/10 hover:border-[#22c55e]/50 transition-all group cursor-default backdrop-blur-sm"
      style={{ background: "rgba(255,255,255,0.025)" }}
    >
      <div className="flex items-start justify-between mb-5">
        <span
          className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
          style={{ background: `${color}18`, color }}
        >
          <Icon size={22} />
        </span>
        <span
          className="rounded-full px-3 py-1 text-xs font-display tracking-widest"
          style={{ background: `${tagColor}15`, color: tagColor, border: `1px solid ${tagColor}28` }}
        >
          {tag}
        </span>
      </div>
      <h3 className="brand-title text-lg group-hover:text-[#22c55e] transition-colors">{title}</h3>
      <p className="text-white/60 text-sm mt-2 leading-relaxed">{desc}</p>
    </div>
  );
}
